import { useMemo, useState } from 'react';

export interface BarSeries {
  key: string;
  label: string;
  /** A CSS colour, normally a --sx-chart-* token so light and dark both hold. */
  color: string;
  values: number[];
}

interface BarChartProps {
  title: string;
  /** One label per column, e.g. "2026-09-30". */
  categories: string[];
  series: BarSeries[];
  /** How a value reads in the tooltip, the axis and the table. */
  format: (value: number) => string;
  /** Shorter axis labels, e.g. "30 Sep". */
  categoryLabel?: (category: string) => string;
  height?: number;
  /** Counts: the axis steps in whole numbers only. */
  integer?: boolean;
}

const WIDTH = 720;
const PAD = { top: 12, right: 12, bottom: 28, left: 56 };

/** A "nice" axis maximum: 1, 2, 2.5 or 5 × a power of ten, at or above the data. */
function niceMax(value: number): number {
  if (value <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (step * power >= value) return step * power;
  }
  return 10 * power;
}

/** A bar with rounded data-end corners, square at the baseline. */
function barPath(x: number, y: number, w: number, h: number): string {
  if (h <= 0) return '';
  const r = Math.min(4, w / 2, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

/**
 * Vertical bar chart, one or more series side by side per category.
 *
 * One value axis only. Hovering anywhere in a column shows that column's
 * values; the chart can also be read as a table, so no value depends on
 * colour or on hovering.
 */
export function BarChart({
  title,
  categories,
  series,
  format,
  categoryLabel,
  height = 220,
  integer = false,
}: BarChartProps) {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);

  const max = useMemo(() => {
    const top = Math.max(0, ...series.flatMap((s) => s.values));
    // Four equal steps: for counts, round the top up to a multiple of 4 so
    // every tick is a whole number (0, 2, 4, 6, 8 rather than 1.3, 2.5…).
    return integer ? Math.max(4, Math.ceil(niceMax(top) / 4) * 4) : niceMax(top);
  }, [series, integer]);
  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;
  const column = plotW / Math.max(1, categories.length);
  const gap = 2;
  const groupW = Math.max(2, Math.min(column * 0.72, 44));
  const barW = Math.max(1, (groupW - gap * (series.length - 1)) / series.length);
  const y = (value: number) => PAD.top + plotH - (value / max) * plotH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * max);
  // At most ~8 category labels, evenly spaced, always including the last.
  const every = Math.max(1, Math.ceil(categories.length / 8));
  const label = categoryLabel ?? ((c: string) => c);
  const empty = series.every((s) => s.values.every((v) => v === 0));

  return (
    <figure className="barchart">
      <figcaption className="barchart__head">
        <span className="barchart__title">{title}</span>
        <span className="barchart__tools">
          {series.length > 1 ? (
            <span className="barchart__legend" aria-hidden={asTable}>
              {series.map((s) => (
                <span key={s.key} className="barchart__key">
                  <span className="barchart__swatch" style={{ background: s.color }} />
                  {s.label}
                </span>
              ))}
            </span>
          ) : null}
          <button className="iconbtn" onClick={() => setAsTable((value) => !value)}>
            {asTable ? 'Show chart' : 'Show as table'}
          </button>
        </span>
      </figcaption>

      {asTable ? (
        <div className="tablewrap barchart__table">
          <table className="table">
            <thead>
              <tr>
                <th>Day</th>
                {series.map((s) => (
                  <th key={s.key}>{s.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories.map((category, i) => (
                <tr key={category}>
                  <td>{label(category)}</td>
                  {series.map((s) => (
                    <td key={s.key}>{format(s.values[i] ?? 0)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="barchart__plot" onMouseLeave={() => setHover(null)}>
          <svg
            viewBox={`0 0 ${WIDTH} ${height}`}
            role="img"
            aria-label={`${title}. ${empty ? 'No activity in this period.' : 'Use "Show as table" for the values.'}`}
          >
            {ticks.map((tick) => (
              <g key={tick}>
                <line
                  x1={PAD.left}
                  x2={WIDTH - PAD.right}
                  y1={y(tick)}
                  y2={y(tick)}
                  className={tick === 0 ? 'barchart__baseline' : 'barchart__grid'}
                />
                <text
                  x={PAD.left - 8}
                  y={y(tick)}
                  className="barchart__axis"
                  textAnchor="end"
                  dominantBaseline="middle"
                >
                  {format(tick)}
                </text>
              </g>
            ))}

            {categories.map((category, i) => {
              const x0 = PAD.left + i * column + (column - groupW) / 2;
              return (
                <g key={category}>
                  {series.map((s, j) => {
                    const value = s.values[i] ?? 0;
                    return (
                      <path
                        key={s.key}
                        d={barPath(
                          x0 + j * (barW + gap),
                          y(value),
                          barW,
                          PAD.top + plotH - y(value),
                        )}
                        fill={s.color}
                        opacity={hover === null || hover === i ? 1 : 0.45}
                      />
                    );
                  })}
                  {/* The last day is always labelled; a regular label too close to it is dropped. */}
                  {i === categories.length - 1 ||
                  (i % every === 0 && categories.length - 1 - i >= every) ? (
                    <text
                      x={PAD.left + i * column + column / 2}
                      y={height - 8}
                      className="barchart__axis"
                      textAnchor="middle"
                    >
                      {label(category)}
                    </text>
                  ) : null}
                  {/* The hit target is the whole column, far larger than a thin bar. */}
                  <rect
                    x={PAD.left + i * column}
                    y={PAD.top}
                    width={column}
                    height={plotH}
                    fill="transparent"
                    onMouseEnter={() => setHover(i)}
                  />
                </g>
              );
            })}
          </svg>

          {hover !== null ? (
            <div
              className="barchart__tip"
              role="status"
              style={{
                left: `${((PAD.left + hover * column + column / 2) / WIDTH) * 100}%`,
                transform: hover > categories.length / 2 ? 'translateX(-100%)' : undefined,
              }}
            >
              <strong>{label(categories[hover]!)}</strong>
              {series.map((s) => (
                <span key={s.key} className="barchart__tiprow">
                  <span className="barchart__swatch" style={{ background: s.color }} />
                  {s.label}: <b>{format(s.values[hover] ?? 0)}</b>
                </span>
              ))}
            </div>
          ) : null}
          {empty ? <p className="barchart__empty muted">No activity in this period.</p> : null}
        </div>
      )}
    </figure>
  );
}
